/**
 * Brewery packaged (can / bottle) production cost — our cost, not the venue's.
 * Same beer chain as kegs; packaging + $0.75/L 3PL; seed Off Prem at 40% our GP.
 */

import { KEG_PRODUCTION_DEFAULTS } from './kegProductionCost';

export const PACKAGED_PRODUCTION_DEFAULTS = {
  wagesPerLitre: KEG_PRODUCTION_DEFAULTS.wagesPerLitre,
  excisePerLitreAlcohol: KEG_PRODUCTION_DEFAULTS.excisePerLitreAlcohol,
  threePlPerLitre: 0.75,
  grossMarginPct: 40,
};

export const PACKAGED_COST_EDITOR_FIELDS = [
  {
    key: 'wagesPerLitre',
    label: 'Wages /L',
    kind: 'money',
    step: 0.1,
  },
  {
    key: 'excisePerLitreAlcohol',
    label: 'Excise /L alcohol',
    kind: 'money',
    step: 0.001,
  },
  {
    key: 'wastagePct',
    label: 'Wastage',
    kind: 'pct',
    step: 0.5,
  },
  {
    key: 'threePlPerLitre',
    label: '3PL /L',
    kind: 'money',
    step: 0.05,
  },
  {
    key: 'grossMarginPct',
    label: 'GP margin',
    kind: 'pct',
    step: 0.5,
  },
];

const CAN_WASTAGE_PCT = 7.5;
const BOTTLE_WASTAGE_PCT = 0;

function unitComp(key, label, rate, units) {
  return {
    key, label, rate, per: 'unit', units,
  };
}

function caseComp(key, label, rate) {
  return {
    key, label, rate, per: 'case', units: 1,
  };
}

export function packagingLineQty(component) {
  if (!component) return 0;
  if (component.per === 'case') return 1;
  const n = Number(component.units);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function packagingLineRate(component, overrides) {
  if (!component) return 0;
  if (!overrides || !Object.prototype.hasOwnProperty.call(overrides, component.key)) {
    return component.rate;
  }
  const n = Number(overrides[component.key]);
  if (!Number.isFinite(n) || n < 0) return component.rate;
  return n;
}

export function packagingLineTotal(component, overrides) {
  return packagingLineRate(component, overrides) * packagingLineQty(component);
}

export function packagingTotal(components, overrides) {
  if (!Array.isArray(components)) return 0;
  return components.reduce((sum, c) => sum + packagingLineTotal(c, overrides), 0);
}

export function resolvePackagingComponents(components, overrides) {
  if (!Array.isArray(components)) return [];
  return components.map((c) => {
    const rate = packagingLineRate(c, overrides);
    const qty = packagingLineQty(c);
    const overridden = Boolean(
      overrides
      && Object.prototype.hasOwnProperty.call(overrides, c.key)
      && Number.isFinite(Number(overrides[c.key]))
      && Number(overrides[c.key]) >= 0,
    );
    return {
      ...c,
      rate,
      qty,
      total: rate * qty,
      overridden,
    };
  });
}

function packagedFormat(litres, wastagePct, components) {
  return {
    litres,
    wastagePct,
    packagingComponents: components,
    packaging: packagingTotal(components),
  };
}

/** Unit rates → case packaging. 4×6 330 omitted on purpose. */
export const PACKAGED_FORMATS = {
  'can|440|1|15x1': packagedFormat(15 * 0.44, CAN_WASTAGE_PCT, [
    unitComp('can', 'Can', 0.27, 15),
    unitComp('lid', 'lid', 0.04, 15),
    unitComp('label', 'Label', 0.29, 15),
    caseComp('carton', 'Carton', 0.52),
  ]),
  'can|440|1|24x1': packagedFormat(24 * 0.44, CAN_WASTAGE_PCT, [
    unitComp('can', 'Can', 0.27, 24),
    unitComp('lid', 'lid', 0.04, 24),
    unitComp('label', 'Label', 0.29, 24),
    caseComp('carton', 'Carton', 0.70),
  ]),
  'can|330|1|24x1': packagedFormat(24 * 0.33, CAN_WASTAGE_PCT, [
    unitComp('can', 'Can', 0.16, 24),
    unitComp('lid', 'lid', 0.04, 24),
    unitComp('label', 'Label', 0.1931, 24),
    caseComp('carton', 'Carton', 0.5331),
  ]),
  'bottle|375|1|12x1': packagedFormat(12 * 0.375, BOTTLE_WASTAGE_PCT, [
    unitComp('bottle', 'Bottle', 1.3, 12),
    caseComp('carton', 'Carton', 0.75),
    unitComp('lid', 'lid', 0.06, 12),
    unitComp('labelFront', 'Label (front)', 0.237, 12),
    unitComp('labelBack', 'Label (back)', 0.18266, 12),
  ]),
  'bottle|750|1|6x1': packagedFormat(6 * 0.75, BOTTLE_WASTAGE_PCT, [
    unitComp('bottle', 'Bottle', 1.78, 6),
    caseComp('carton', 'Carton', 0.75),
    unitComp('crown', 'Crown', 0.06, 6),
    unitComp('labelFront', 'Label (front)', 0.45, 6),
    unitComp('labelBack', 'Label (back)', 0.45, 6),
  ]),
};

function formatPart(sku, camel) {
  const snake = camel.replace(/[A-Z]/g, (ch) => `_${ch.toLowerCase()}`);
  return String(sku?.[camel] ?? sku?.[snake] ?? '').trim().toLowerCase();
}

export function packagedFormatKey(sku) {
  if (!sku) return '';
  return ['containerType', 'unitType', 'packType', 'caseType']
    .map((key) => formatPart(sku, key))
    .join('|');
}

export function resolvePackagedFormat(sku) {
  const spec = PACKAGED_FORMATS[packagedFormatKey(sku)];
  return spec || null;
}

/**
 * @param {{
 *   costPerFinishedL: number|null|undefined,
 *   abvPct: number|null|undefined,
 *   sku: object,
 *   settings?: object,
 * }} input
 * @returns {{
 *   caseCost: number,
 *   caseSell: number,
 *   breakdown: {
 *     costPerFinishedL: number,
 *     wagesPerLitre: number,
 *     costPerLInclWages: number,
 *     beerCostInCase: number,
 *     excise: number,
 *     packaging: number,
 *     packagingComponents: object[],
 *     threePl: number,
 *     litres: number,
 *     wastagePct: number,
 *   },
 * }|null}
 */
export function computePackagedProductionCost({
  costPerFinishedL,
  abvPct,
  sku,
  settings = {},
}) {
  const format = resolvePackagedFormat(sku);
  if (!format) return null;
  if (costPerFinishedL == null || !Number.isFinite(costPerFinishedL) || costPerFinishedL < 0) {
    return null;
  }
  if (abvPct == null || !Number.isFinite(abvPct) || abvPct < 0) return null;

  const wagesPerLitre = settings.wagesPerLitre ?? PACKAGED_PRODUCTION_DEFAULTS.wagesPerLitre;
  const excisePerLitreAlcohol = settings.excisePerLitreAlcohol
    ?? PACKAGED_PRODUCTION_DEFAULTS.excisePerLitreAlcohol;
  const threePlPerLitre = settings.threePlPerLitre ?? PACKAGED_PRODUCTION_DEFAULTS.threePlPerLitre;
  const grossMarginPct = settings.grossMarginPct ?? PACKAGED_PRODUCTION_DEFAULTS.grossMarginPct;
  const wastagePct = settings.wastagePct ?? format.wastagePct;

  const wastageFrac = wastagePct / 100;
  const marginFrac = grossMarginPct / 100;
  if (!(wastageFrac >= 0 && wastageFrac < 1) || !(marginFrac >= 0 && marginFrac < 1)) {
    return null;
  }

  const { litres } = format;
  const packagingComponents = resolvePackagingComponents(
    format.packagingComponents,
    settings.packagingComponents,
  );
  const packaging = packagingTotal(format.packagingComponents, settings.packagingComponents);
  const costPerLInclWages = costPerFinishedL + wagesPerLitre;
  const beerCostInCase = (costPerLInclWages / (1 - wastageFrac)) * litres;
  const excise = (abvPct / 100) * excisePerLitreAlcohol * litres;
  const threePl = threePlPerLitre * litres;
  const caseCost = beerCostInCase + excise + packaging + threePl;
  const caseSell = caseCost / (1 - marginFrac);

  return {
    caseCost,
    caseSell,
    breakdown: {
      costPerFinishedL,
      wagesPerLitre,
      costPerLInclWages,
      beerCostInCase,
      excise,
      packaging,
      packagingComponents,
      threePl,
      litres,
      wastagePct,
    },
  };
}
